import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, Loader2, X } from "lucide-react";

const debounce = (fn, ms = 400) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

export default function Signup() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ business_name: "", slug: "", owner_name: "", email: "", password: "", referral_code: "", signup_code: (searchParams.get("code") || "").trim().toUpperCase() });
  const [accepted, setAccepted] = useState(false);
  const [slugState, setSlugState] = useState({ checking: false, available: null, valid_format: null });
  const [signupCodeState, setSignupCodeState] = useState({ checking: false, valid: null, message: "", kind: null });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsHtml, setTermsHtml] = useState("");

  const checkSignupCode = useMemo(() => debounce(async (c) => {
    if (!c) { setSignupCodeState({ checking: false, valid: null, message: "", kind: null }); return; }
    try {
      const { data } = await api.post("/platform/validate-signup-code", { code: c });
      setSignupCodeState({ checking: false, valid: true, message: data.perks_description, kind: data.kind });
    } catch (e) {
      setSignupCodeState({ checking: false, valid: false, message: e.response?.data?.detail || "Código inválido", kind: null });
    }
  }, 400), []);

  useEffect(() => {
    const c = form.signup_code.trim().toUpperCase();
    if (c) {
      setSignupCodeState((p) => ({ ...p, checking: true }));
      checkSignupCode(c);
    } else {
      setSignupCodeState({ checking: false, valid: null, message: "", kind: null });
    }
  }, [form.signup_code, checkSignupCode]);

  const checkSlug = useMemo(() => debounce(async (s) => {
    if (!s) { setSlugState({ checking: false, available: null, valid_format: null }); return; }
    try {
      const { data } = await api.get(`/platform/check-slug/${encodeURIComponent(s)}`);
      setSlugState({ checking: false, available: data.available, valid_format: data.valid_format });
    } catch {
      setSlugState({ checking: false, available: false, valid_format: false });
    }
  }, 350), []);

  useEffect(() => {
    const s = form.slug.toLowerCase().trim();
    if (s) {
      setSlugState((p) => ({ ...p, checking: true }));
      checkSlug(s);
    } else {
      setSlugState({ checking: false, available: null, valid_format: null });
    }
  }, [form.slug, checkSlug]);

  async function openTerms(e) {
    e?.preventDefault();
    setTermsOpen(true);
    if (!termsHtml) {
      try {
        const { data } = await api.get("/platform/terms");
        setTermsHtml(data.terms_html || "");
      } catch { setTermsHtml("<p>No pudimos cargar los términos.</p>"); }
    }
  }

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    if (slugState.available === false) { setErr("Esta URL no está disponible"); return; }
    if (!accepted) { setErr("Debes aceptar los términos y condiciones"); return; }
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/platform/signup", {
        ...form,
        slug: form.slug.toLowerCase().trim(),
        referral_code: form.referral_code.trim() || undefined,
        signup_code: (signupCodeState.valid && form.signup_code.trim().toUpperCase()) || undefined,
        accepted_terms: true,
      });
      setUser(data.user);
      toast.success(data.referral_applied ? "¡Cuenta creada! Cupón aplicado: 50% en tu primer mes." : "Cuenta creada. ¡Bienvenido!");
      navigate("/admin", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 sm:p-12">
        <Card className="w-full max-w-lg border-slate-200">
          <CardContent className="p-8">
            <Link to="/" className="overline" data-testid="signup-back-home">← Plataforma Book-It</Link>
            <h1 className="font-display text-3xl font-light mt-4">Crea tu cuenta de negocio</h1>
            <p className="text-slate-600 mt-2 text-sm">30 días de prueba gratis. Configura tu URL y empieza a recibir citas.</p>

            <form className="mt-8 space-y-4" onSubmit={onSubmit}>
              <div className="space-y-2">
                <Label htmlFor="bn">Nombre del negocio</Label>
                <Input id="bn" required value={form.business_name} onChange={(e) => update("business_name", e.target.value)} data-testid="signup-business-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Tu URL (book-it.com.mx/<span className="font-semibold text-[hsl(var(--accent))]">tu-negocio</span>)</Label>
                <div className="relative">
                  <Input
                    id="slug"
                    required
                    placeholder="mi-negocio"
                    value={form.slug}
                    onChange={(e) => update("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    className="pr-10"
                    data-testid="signup-slug-input"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {slugState.checking && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                    {!slugState.checking && slugState.available === true && <Check className="h-4 w-4 text-emerald-600" data-testid="slug-ok" />}
                    {!slugState.checking && slugState.available === false && <X className="h-4 w-4 text-red-600" data-testid="slug-bad" />}
                  </div>
                </div>
                {form.slug && slugState.valid_format === false && <p className="text-xs text-red-600">Solo letras, números y guiones. 3-30 caracteres.</p>}
                {form.slug && slugState.valid_format && slugState.available === false && <p className="text-xs text-red-600">Esta URL ya está en uso.</p>}
                {form.slug && slugState.available === true && <p className="text-xs text-emerald-700">¡Disponible!</p>}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="on">Tu nombre</Label>
                  <Input id="on" required value={form.owner_name} onChange={(e) => update("owner_name", e.target.value)} data-testid="signup-owner-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="em">Correo</Label>
                  <Input id="em" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="signup-email-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pw">Contraseña</Label>
                <Input id="pw" type="password" required minLength={6} value={form.password} onChange={(e) => update("password", e.target.value)} data-testid="signup-password-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rc" className="text-slate-600">Código de referido (opcional · 50% en tu primer mes)</Label>
                <Input id="rc" placeholder="BOOKIT-XXXXXX" value={form.referral_code} onChange={(e) => update("referral_code", e.target.value.toUpperCase())} data-testid="signup-referral-input" />
              </div>

              {!signupCodeState.checking && signupCodeState.valid === true && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="signup-special-code-valid">
                  ✓ {signupCodeState.message}
                </div>
              )}

              {err && <p className="text-sm text-red-700" data-testid="signup-error">{err}</p>}

              <label className="flex items-start gap-3 cursor-pointer text-sm rounded-md border border-slate-200 bg-slate-50 px-4 py-3" data-testid="signup-terms-row">
                <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} data-testid="signup-terms-checkbox" />
                <span className="leading-snug text-slate-700">
                  He leído y acepto los{" "}
                  <button type="button" onClick={openTerms} className="underline text-[hsl(var(--accent))] font-medium" data-testid="signup-terms-link">términos y condiciones</button>{" "}
                  de la plataforma Book-It.
                </span>
              </label>

              <Button type="submit" className="w-full" disabled={loading || slugState.available === false || !accepted} data-testid="signup-submit-btn">
                {loading ? "Creando…" : "Crear negocio · Trial 30 días"}
              </Button>
            </form>

            <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
                <DialogHeader><DialogTitle>Términos y Condiciones · Book-It</DialogTitle></DialogHeader>
                <div className="prose prose-sm max-w-none" data-testid="signup-terms-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(termsHtml || "<p>Cargando…</p>") }} />
                <DialogFooter>
                  <Button onClick={() => { setAccepted(true); setTermsOpen(false); }} data-testid="signup-terms-accept">Aceptar términos</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <p className="text-sm text-slate-600 mt-6">
              ¿Ya tienes cuenta? <Link to="/login" className="text-primary underline" data-testid="signup-go-login">Iniciar sesión</Link>
            </p>
          </CardContent>
        </Card>
      </div>
      <div className="hidden lg:flex items-center justify-center p-12 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-md">
          <BrandLogo subtitle size={48} />
          <h2 className="font-display text-4xl font-light mt-8 leading-tight">
            Tu negocio.<br />Tu URL.<br /><span className="italic text-[hsl(var(--accent))]">Tu agenda.</span>
          </h2>
          <ul className="mt-8 space-y-3 text-slate-700 text-sm">
            <li className="flex gap-3"><Check className="h-5 w-5 text-[hsl(var(--accent))] shrink-0" /> URL personalizada para tus clientes</li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[hsl(var(--accent))] shrink-0" /> Cobros con tu propia cuenta Stripe</li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[hsl(var(--accent))] shrink-0" /> Sin costo por 30 días</li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[hsl(var(--accent))] shrink-0" /> $200 MXN/mes + $30 por profesional</li>
            <li className="flex gap-3"><Check className="h-5 w-5 text-[hsl(var(--accent))] shrink-0" /> 1 mes gratis por cada referido</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
